// src/components/TipPortal.js
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Tooltip wrapper that portals to <body> and positions via fixed coords.
export default function TipPortal({ text, children, side = "right" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: -9999, left: -9999 });
  const wrapRef = useRef(null);
  const tipRef = useRef(null);
  const hideTimer = useRef(null);

  const show = () => {
    window.clearTimeout(hideTimer.current);
    setOpen(true);
  };

  const hide = () => {
    window.clearTimeout(hideTimer.current);
    setOpen(false);
  };

  const touchPeek = () => {
    show();
    hideTimer.current = window.setTimeout(() => setOpen(false), 1200);
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const compute = () => {
    const anchor = wrapRef.current?.getBoundingClientRect?.();
    const tip = tipRef.current?.getBoundingClientRect?.();
    if (!anchor || !tip) return;

    const gap = 10;

    let top = 0;
    let left = 0;

    if (side === "bottom") {
      top = anchor.bottom + gap;
      left = anchor.left + anchor.width / 2 - tip.width / 2;
    } else if (side === "top") {
      top = anchor.top - tip.height - gap;
      left = anchor.left + anchor.width / 2 - tip.width / 2;
    } else if (side === "left") {
      top = anchor.top + anchor.height / 2 - tip.height / 2;
      left = anchor.left - tip.width - gap;
    } else {
      // right (default)
      top = anchor.top + anchor.height / 2 - tip.height / 2;
      left = anchor.right + gap;
    }

    // keep inside viewport
    const pad = 8;
    left = clamp(left, pad, window.innerWidth - tip.width - pad);
    top = clamp(top, pad, window.innerHeight - tip.height - pad);

    setPos({ top, left });
  };

  // compute after tooltip mounts
  useLayoutEffect(() => {
    if (!open) return;
    // wait a frame so tipRef has size
    const raf = requestAnimationFrame(() => compute());
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side, text]);

  // recompute on scroll/resize while open
  useEffect(() => {
    if (!open) return;

    const onMove = () => compute();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    window.addEventListener("orientationchange", onMove);

    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("orientationchange", onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, side]);

  return (
    <span
      ref={wrapRef}
      className={`tcp-tipwrap tcp-tipwrap--${side}`}
      onPointerEnter={(e) => e.pointerType !== "touch" && show()}
      onPointerLeave={() => hide()}
      onFocusCapture={() => show()}
      onBlurCapture={() => hide()}
      onPointerDown={(e) => e.pointerType === "touch" && touchPeek()}
    >
      {children}

      {open &&
        createPortal(
          <span
            ref={tipRef}
            className={`tcp-tip tcp-tip--${side}`}
            role="tooltip"
            style={{
              top: `${pos.top}px`,
              left: `${pos.left}px`,
            }}
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
}
